import { useEffect, useRef } from "react";
import { Quote } from "lucide-react";
import { FadeUp } from "../Reveal";
import { testimonials } from "../../data/content";
import { useReducedMotion } from "../../hooks/useReducedMotion";

const Card = ({ t, compact = false }) => (
  <div className={`shrink-0 rounded-2xl border border-edge-650 bg-graphite-900/60 p-6 ${compact ? "w-full" : "w-[320px] sm:w-[420px] mr-4"}`}>
    <Quote className="w-5 h-5 text-magenta-500" />
    <p className="mt-4 text-zinc-200 leading-relaxed text-sm sm:text-[15px]">“{t.quote}”</p>
    <div className="mt-5 flex items-center gap-3">
      <span className="inline-flex w-9 h-9 rounded-full border border-edge-700 bg-ink-900 items-center justify-center font-mono text-[11px] text-magenta-300">{t.initials}</span>
      <div>
        <div className="text-sm text-white">{t.name}</div>
        <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-zinc-500">{t.role}</div>
      </div>
    </div>
  </div>
);

export const Testimonials = () => {
  const reduced = useReducedMotion();
  const trackRef = useRef(null);
  const items = [...testimonials, ...testimonials];

  useEffect(() => {
    const el = trackRef.current;
    if (!el || reduced) return;
    const onEnter = () => (el.style.animationPlayState = "paused");
    const onLeave = () => (el.style.animationPlayState = "running");
    el.addEventListener("mouseenter", onEnter);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mouseenter", onEnter);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, [reduced]);

  return (
    <section id="testimonials" className="relative py-20 sm:py-28 border-t border-edge-650 overflow-hidden">
      <div className="container-page">
        <FadeUp>
          <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-magenta-300">Voices</div>
        </FadeUp>
        <FadeUp delay={0.05}>
          <h2 className="mt-4 font-display tracking-tight text-3xl sm:text-5xl text-white max-w-3xl leading-[1.05]">
            What people say when I’m not in the room.
          </h2>
        </FadeUp>
      </div>

      <div className="mt-12 relative" data-testid="testimonials-carousel">
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-24 sm:w-40 bg-gradient-to-r from-ink-950 to-transparent z-10" />
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-24 sm:w-40 bg-gradient-to-l from-ink-950 to-transparent z-10" />

        {reduced ? (
          <div className="container-page grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {testimonials.map((t) => (<Card key={t.name} t={t} compact />))}
          </div>
        ) : (
          <div ref={trackRef} className="flex animate-marquee whitespace-nowrap" style={{ width: "max-content" }}>
            {items.map((t, i) => (
              <div key={i} className="whitespace-normal"><Card t={t} /></div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
